import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatMessage } from '../utils/messageTemplates';
import { getCryptoIcons } from '../utils/botIcons';

// Расширяем тип Window для нашего обработчика
declare global {
  interface Window {
    navigateToInstructions?: () => void;
  }
}

interface ChatMessageProps {
  message: {
    id: number;
    sender_type: 'user' | 'bot';
    message: string;
    timestamp_msk: string;
    template_key?: string;
    template_params?: string;
    message_type?: string;
  };
  botDisplayName: string;
  botName?: string; // Добавляем имя бота для получения иконок
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, botDisplayName, botName }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Используем общую утилиту для получения иконок ботов

  // Форматирование времени с учетом часового пояса пользователя
  const formatTime = (moscowTime: string) => {
    // Парсим московское время и конвертируем в локальное
    // Формат: "2024-01-01 12:34:56"
    const [datePart, timePart] = moscowTime.split(' ');
    const [year, month, day] = datePart.split('-');
    const [hour, minute, second] = timePart.split(':');
    
    // Создаем дату в UTC (московское время +3 часа)
    const utcDate = new Date(Date.UTC(
      parseInt(year), 
      parseInt(month) - 1, // месяц начинается с 0
      parseInt(day),
      parseInt(hour) - 3, // вычитаем 3 часа для конвертации из московского в UTC
      parseInt(minute),
      parseInt(second)
    ));
    
    // Получаем локальную дату/время пользователя
    const now = new Date();
    const isToday = utcDate.toDateString() === now.toDateString();
    if (isToday) {
      return utcDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
    const dayStr = String(utcDate.getDate()).padStart(2, '0');
    const monthStr = String(utcDate.getMonth() + 1).padStart(2, '0');
    const hoursStr = String(utcDate.getHours()).padStart(2, '0');
    const minutesStr = String(utcDate.getMinutes()).padStart(2, '0');
    return `${dayStr}/${monthStr} ${hoursStr}:${minutesStr}`;
  };

  // Форматирование сообщения с подстановкой параметров
  const formatMessageText = (templateKey: string | undefined, templateParams: string | undefined) => {
    if (!templateKey) return message.message;
    
    let params = {};
    if (templateParams) {
      try {
        params = JSON.parse(templateParams);
      } catch (e) {
        console.error('Error parsing template params:', e);
      }
    }
    
    const language = i18n.language === 'ENG' ? 'ENG' : 'RUS';
    return formatMessage(templateKey as string, params, language);
  };

  const displayMessage = formatMessageText(message.template_key, message.template_params);
  const displayTime = formatTime(message.timestamp_msk);

  // Обработчик клика по ссылке инструкций
  React.useEffect(() => {
    window.navigateToInstructions = () => {
      navigate('/instructions');
    };
    
    return () => {
      delete window.navigateToInstructions;
    };
  }, [navigate]);

  return (
    <div className="chat-message flex gap-3 mb-4" data-mid={message.id} id={`msg-${message.id}`}>
      <div className="message-content flex-1 min-w-0">
        <div className={`p-3 rounded-lg ${
          message.sender_type === 'user'
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-text'
        }`}>
          <div className="message-sender font-bold text-sm mb-0.5 flex items-center">
            {message.sender_type === 'user' 
              ? (i18n.language === 'ENG' ? 'Your message:' : 'Ваше сообщение:')
              : (
                <>
                  {botName && (
                    <>
                      <img 
                        src={getCryptoIcons(botName).first} 
                        alt="First currency" 
                        className="w-[14px] h-[14px]" 
                        style={{ width: '14px', height: '14px', ...getCryptoIcons(botName).firstStyle }} 
                      />
                      <span>{botDisplayName}</span>
                      <img 
                        src={getCryptoIcons(botName).second} 
                        alt="Second currency" 
                        className="w-[14px] h-[14px]" 
                        style={{ width: '14px', height: '14px', ...getCryptoIcons(botName).secondStyle }} 
                      />
                    </>
                  )}
                  :
                </>
              )
            }
          </div>
          <div 
            className="text-sm" 
            dangerouslySetInnerHTML={{ __html: displayMessage }}
          />
        </div>
      </div>
              <div className="message-time w-[60px] text-right text-xs opacity-70 flex-shrink-0 whitespace-nowrap self-start pt-4">
          {displayTime}
        </div>
    </div>
  );
}; 